import React, { useMemo, useState } from 'react';
import ReceivablesTable from '../components/ReceivablesTable';
import useReceivables from '../hooks/useReceivables';
import ReceivableFormModal from '../components/ReceivableFormModal';
import ReceivableDetailModal from '../components/ReceivableDetailModal';
import FlashMessage from '../components/FlashMessage';
import SummaryCard from '../components/SummaryCard';
import {
  PageContainer,
  ContentContainer,
  Header,
  AddButton,
  Label,
} from '../styles/ComponentStyles';
import apiService from '../services/apiService';
import { summarizeReceivables, formatMoney } from '../utils/receivableMath';

const Receivables = ({ isSidebarOpen }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageLimit, setCurrentPageLimit] = useState(100);
  const { receivables, totalItems, totalPages, loading, error } = useReceivables(
    currentPage,
    currentPageLimit,
    refreshKey
  );
  const [flashMessage, setFlashMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [detailReceivable, setDetailReceivable] = useState(null);

  const summary = useMemo(() => summarizeReceivables(receivables), [receivables]);

  const refreshData = () => setRefreshKey((k) => k + 1);

  const handleFlashMessage = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 3000);
  };

  const handlePageChange = (p) => setCurrentPage(p);
  const handlePageLimitChange = (l) => setCurrentPageLimit(l);

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleViewDetail = (rec) => setDetailReceivable(rec);
  const handleCloseDetail = () => setDetailReceivable(null);

  const handleSubmit = async (payload) => {
    try {
      if (editingItem) {
        await apiService.updateReceivable(editingItem._id, payload);
        handleFlashMessage('Receivable updated.');
      } else {
        await apiService.createReceivable(payload);
        handleFlashMessage('Receivable added.');
      }
      refreshData();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Something went wrong';
      handleFlashMessage(`Error: ${msg}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiService.deleteReceivable(id);
      handleFlashMessage(res?.data?.message || res?.message || 'Deleted.');
      refreshData();
    } catch (err) {
      handleFlashMessage(err?.response?.data?.detail || 'Error deleting.');
    }
  };

  return (
    <PageContainer>
      <ContentContainer isSidebarOpen={isSidebarOpen}>
        <Header>
          <h1>Receivables</h1>
          <AddButton onClick={handleAdd}>Add New</AddButton>
        </Header>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <SummaryCard
            title="Total Outstanding"
            value={
              <span style={{ color: '#f44336' }}>
                {formatMoney(summary.totalOutstanding)}
              </span>
            }
          />
          <SummaryCard
            title="Total Collected"
            value={
              <span style={{ color: '#4CAF50' }}>
                {formatMoney(summary.totalCollected)}
              </span>
            }
          />
          <SummaryCard title="Active" value={summary.activeCount} />
        </div>

        <div>
          <Label>Total: {totalItems}</Label>
          <Label>Pages: {totalPages}</Label>
        </div>
        {flashMessage && <FlashMessage message={flashMessage} />}
        <ReceivablesTable
          receivables={receivables}
          loading={loading}
          error={error}
          handleDelete={handleDelete}
          currentPage={currentPage}
          currentPageLimit={currentPageLimit}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageLimitChange={handlePageLimitChange}
          onEdit={handleEdit}
          onViewDetail={handleViewDetail}
        />
      </ContentContainer>

      {showModal && (
        <ReceivableFormModal
          editing={!!editingItem}
          initialData={editingItem}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}

      {detailReceivable && (
        <ReceivableDetailModal
          receivable={detailReceivable}
          onClose={handleCloseDetail}
          onRefresh={refreshData}
        />
      )}
    </PageContainer>
  );
};

export default Receivables;
